import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import NewsPage from './pages/NewsPage';
import ReportsPage from './pages/ReportsPage';
import SectionsPage from './pages/SectionsPage';
// import TransitionalJusticePage from './pages/TransitionalJusticePage';
import DigitalRightsPage from './pages/DigitalRightsPage';
import AboutUsPage from './pages/AboutUsPage';
import ContactUsPage from './pages/ContactUsPage';
// AdminPanel import removed - no longer needed
import Login from './pages/admin/Login';
import Dashboard from './pages/admin/Dashboard';
import AboutEdit from './pages/admin/AboutEdit';
import UserManagement from './pages/admin/UserManagement';
import Settings from './pages/admin/Settings';
import AdminLayout from './pages/admin/AdminLayout';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { SiteSettingsProvider, useSiteSettings } from './hooks/useSiteSettings';
import Sections from './pages/admin/Sections';
import SectionDetailsPage from './pages/SectionDetailsPage';
import AddArticle from './pages/admin/AddArticle';
import ActivityLog from './pages/admin/ActivityLog';
import ArticleManagement from './pages/admin/ArticleManagement';
import EditArticle from './pages/admin/EditArticle';
import NewsManagement from './pages/admin/NewsManagement';
import AddNews from './pages/admin/AddNews';
import EditNews from './pages/admin/EditNews';
import SocialSidebar from './components/SocialSidebar';
import ReportDetails from './pages/ReportDetails';
import ArticleDetailsPage from './pages/ArticleDetailsPage';
import NewsDetailsPage from './pages/NewsDetailsPage';
import PermissionsPage from './pages/admin/PermissionsPage';
import BackupPage from './pages/admin/BackupPage';
import Profile from './pages/admin/Profile';
import MessagesPage from './pages/admin/MessagesPage';
import ReportOverview from './pages/admin/reports/Overview';
import UsersReport from './pages/admin/reports/UsersReport';
import ActivityLogReport from './pages/admin/reports/ActivityLogReport';
import NewsReport from './pages/admin/reports/NewsReport';
import ReportsListReport from './pages/admin/reports/ReportsListReport';
import ArticlesReport from './pages/admin/reports/ArticlesReport';
import MessagesReport from './pages/admin/reports/MessagesReport';
import NavManager from './pages/admin/NavManager';
import AdTickerManager from './pages/admin/AdTickerManager';
import AdPopupManager from './pages/admin/AdPopupManager';
import AdTicker from './components/AdTicker';
import AdPopup from './components/AdPopup';

function RequireAdminAuth({ children }) {
  const location = useLocation();
  const { user, loading } = useAuth();

  if (loading) {
    return <div style={{textAlign:'center',marginTop:100,fontSize:24}}>جاري التحقق من الصلاحية...</div>;
  }

  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
}

function AppContent({ darkMode, setDarkMode }) {
  const location = useLocation();
  const { site_name, siteDescription, site_keywords } = useSiteSettings();
  const isAdminRoute = location.pathname.startsWith('/admin');
  return (
    <div className="App">
      <Helmet>
        <title>{site_name || ''}</title>
        {siteDescription && <meta name="description" content={siteDescription} />}
        {site_keywords && <meta name="keywords" content={site_keywords} />}
      </Helmet>
      {!isAdminRoute && <Header darkMode={darkMode} setDarkMode={setDarkMode} />}
      {!isAdminRoute && <AdTicker />}
      <main className="main-content">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/sections" element={<SectionsPage />} />
          <Route path="/reports/:id" element={<ReportDetails />} />
          <Route path="/digital-rights" element={<DigitalRightsPage />} />
          <Route path="/about-us" element={<AboutUsPage />} />
          <Route path="/contact-us" element={<ContactUsPage />} />
          <Route path="/admin/login" element={<Login />} />
          <Route path="/admin/*" element={
            <RequireAdminAuth>
              <AdminLayout />
            </RequireAdminAuth>
          }>
            <Route index element={<Dashboard />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="about-edit" element={<AboutEdit />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="settings" element={<Settings />} />
            <Route path="sections" element={<Sections />} />
            <Route path="add-article" element={<AddArticle />} />
            <Route path="edit-article/:id" element={<EditArticle />} />
            <Route path="activity-log" element={<ActivityLog />} />
            <Route path="articles" element={<ArticleManagement />} />
            <Route path="news" element={<NewsManagement />} />
            <Route path="add-news" element={<AddNews />} />
            <Route path="edit-news/:id" element={<EditNews />} />
            <Route path="permissions" element={<PermissionsPage />} />
            <Route path="backup" element={<BackupPage />} />
            <Route path="profile" element={<Profile />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="internal-reports" element={<ReportOverview />} />
            <Route path="internal-reports/users" element={<UsersReport />} />
            <Route path="internal-reports/activity" element={<ActivityLogReport />} />
            <Route path="internal-reports/news" element={<NewsReport />} />
            <Route path="internal-reports/reports" element={<ReportsListReport />} />
            <Route path="internal-reports/articles" element={<ArticlesReport />} />
            <Route path="internal-reports/messages" element={<MessagesReport />} />
            <Route path="nav-manager" element={<NavManager />} />
            <Route path="ad-ticker" element={<AdTickerManager />} />
            <Route path="ad-popup" element={<AdPopupManager />} />
          </Route>
          <Route path="/sections/:slug" element={<SectionDetailsPage />} />
          <Route path="/articles/:id" element={<ArticleDetailsPage />} />
          <Route path="/news/:id" element={<NewsDetailsPage />} />
        </Routes>
      </main>
      {!isAdminRoute && <Footer />}
      <SocialSidebar />
      {!isAdminRoute && <AdPopup />}
    </div>
  );
}

function App() {
  const [dbStatus, setDbStatus] = useState('checking'); // 'checking' | 'ok' | 'error'
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });

  useEffect(() => {
    const checkDb = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/health`);
        const data = await res.json();
        if (data.status && data.status.toLowerCase() === 'ok') {
          setDbStatus('ok');
        } else {
          setDbStatus('error');
        }
      } catch {
        setDbStatus('error');
      }
    };
    checkDb();
  }, []);

  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  if (dbStatus === 'checking') {
    return <div style={{textAlign:'center',marginTop:100,fontSize:24}}>جاري التحقق من الاتصال بقاعدة البيانات...</div>;
  }
  if (dbStatus === 'error') {
    return <div style={{textAlign:'center',marginTop:100,fontSize:24,color:'red'}}>تعذر الاتصال بقاعدة البيانات. يرجى التأكد من تشغيل السيرفر والمحاولة مرة أخرى.</div>;
  }

  return (
    <SiteSettingsProvider>
      <AuthProvider>
        <Router>
          <AppContent darkMode={darkMode} setDarkMode={setDarkMode} />
        </Router>
      </AuthProvider>
    </SiteSettingsProvider>
  );
}

export default App;
